import { getAuthToken, getStoredUser, getAuthHeaders } from '@/utils/auth';
import { clearAuth } from '@/utils/auth';
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StyledSelect } from '@/components/ui/styled-select';
import { 
  Ship, Palette, Package, TrendingUp, DollarSign, Plus, X, LogOut, BarChart3, Users, Sparkles, ArrowLeft, User, Pencil, Trash2, CheckSquare, Square
} from 'lucide-react';
import Nav from '@/components/Nav';
import axios from 'axios';
import { toast } from 'sonner';
import { useCurrency } from '@/context/CurrencyContext';
import PartnerCRM from '@/components/PartnerCRM';
import PartnerCRMDashboardV2 from '@/components/PartnerCRMDashboardV2';
import AssetMediaManager from '@/components/partner/AssetMediaManager';
import LocationField from '@/components/partner/LocationField';
import BulkUploadModal from '@/components/partner/BulkUploadModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map config.endpoint → asset_type used by the universal media + bulk endpoints.
const ASSET_TYPE_FROM_ENDPOINT = {
  '/partner/cars':      'cars',
  '/partner/yachts':    'yachts',
  '/partner/portfolio': 'portfolio',
  '/partner/services':  'services',
};

// Map config.endpoint → wizard asset type (Partner Asset Template Standardisation).
// Falls back to undefined for roles without a dedicated template; the
// "Add" CTA then keeps using the legacy inline modal.
const WIZARD_ASSET_TYPE_FROM_ENDPOINT = {
  '/partner/cars':      'car',
  '/partner/yachts':    'yacht',
  '/partner/portfolio': 'interior_design',
};

// Map config.endpoint → backend partner-listings asset_type (the path segment
// used by /api/partner-listings/{asset_type}/{id}/validate). Mirrors the
// wizard type but kept separate in case they ever diverge.
const VALIDATE_ASSET_TYPE_FROM_ENDPOINT = WIZARD_ASSET_TYPE_FROM_ENDPOINT;

const DASHBOARD_CONFIG = {
  yacht_dealer: {
    title: 'Yacht Dealer Dashboard',
    portal: 'Yacht Dealer Portal',
    icon: Ship,
    itemName: 'Yacht',
    itemsName: 'Yachts',
    endpoint: '/partner/yachts',
    fields: ['name', 'brand', 'length_ft', 'year', 'price', 'capacity', 'description'],
    accentColor: 'cyan',
    heroImages: [
      'https://images.unsplash.com/photo-1607276319387-8bf6e5a1807d?auto=format&fit=crop&w=1920&q=80',
      'https://images.pexels.com/photos/9928839/pexels-photo-9928839.jpeg?auto=compress&cs=tinysrgb&w=1920',
      'https://images.pexels.com/photos/42091/pexels-photo-42091.jpeg?auto=compress&cs=tinysrgb&w=1920'
    ]
  },
  interior_designer: {
    title: 'Interior Designer Dashboard',
    portal: 'Designer Portal',
    icon: Palette,
    itemName: 'Portfolio Item',
    itemsName: 'Portfolio',
    endpoint: '/partner/portfolio',
    fields: ['title', 'category', 'style', 'budget_range', 'description'],
    accentColor: 'pink',
    heroImages: [
      'https://images.unsplash.com/photo-1672927936377-97d1be3976cd?auto=format&fit=crop&w=1920&q=80',
      'https://images.unsplash.com/photo-1628745277866-ff9305ac52cc?auto=format&fit=crop&w=1920&q=80',
      'https://images.unsplash.com/photo-1628745277926-086b68b2d542?auto=format&fit=crop&w=1920&q=80'
    ]
  },
  other_business: {
    title: 'Business Dashboard',
    portal: 'Business Portal',
    icon: Package,
    itemName: 'Service',
    itemsName: 'Services',
    endpoint: '/partner/services',
    fields: ['service_name', 'category', 'price', 'price_type', 'description'],
    accentColor: 'amber',
    heroImages: [
      'https://images.unsplash.com/photo-1758448656990-398443036eb4?auto=format&fit=crop&w=1920&q=80',
      'https://images.unsplash.com/photo-1758448656987-cfae6bf225e4?auto=format&fit=crop&w=1920&q=80',
      'https://images.unsplash.com/photo-1758194190679-198a77cba84f?auto=format&fit=crop&w=1920&q=80'
    ]
  }
};

const GenericPartnerDashboard = () => {
  const navigate = useNavigate();
  const { formatPrice: formatCurrency } = useCurrency();
  const [user, setUser] = useState(null);
  const [config, setConfig] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [items, setItems] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItem, setNewItem] = useState({});
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  // Per-listing completion-% (v2 wizard items only). Keyed by listing id.
  const [completion, setCompletion] = useState({});

  // Asset-type derived from the active config (cars/yachts/portfolio/services).
  const assetType = config ? ASSET_TYPE_FROM_ENDPOINT[config.endpoint] : null;

  const handleLogout = useCallback(() => {
    clearAuth();
    navigate('/partner/login');
  }, [navigate]);

  const fetchData = useCallback(async () => {
    try {
      const token = getAuthToken();
      const headers = { Authorization: `Bearer ${token}` };
      const userData = JSON.parse(JSON.stringify(getStoredUser()) || '{}');
      const dashConfig = DASHBOARD_CONFIG[userData.role] || DASHBOARD_CONFIG.other_business;
      
      const [itemsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}${dashConfig.endpoint}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/partner/crm/analytics`, { headers })
      ]);
      
      setItems(itemsRes.data);
      setAnalytics(analyticsRes.data);

      // Fetch completion-% for every listing (legacy + v2 wizard) so we can
      // show a progress pill on each card.
      const v2type = VALIDATE_ASSET_TYPE_FROM_ENDPOINT[dashConfig.endpoint];
      if (v2type) {
        const all = itemsRes.data || [];
        if (all.length === 0) {
          setCompletion({});
        } else {
          const entries = await Promise.all(all.map(async (it) => {
            try {
              const { data } = await axios.post(
                `${API}/partner-listings/${v2type}/${it.id}/validate`,
                null,
                { headers },
              );
              return [it.id, data];
            } catch { return [it.id, null]; }
          }));
          const map = {};
          entries.forEach(([id, rep]) => { if (rep) map[id] = rep; });
          setCompletion(map);
        }
      }
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  // Unified card adapter — reads v2 wizard schema (title, cover_image, price)
  // AND falls back to the legacy field names per asset type.
  const adapt = useCallback((item) => {
    const isV2 = !!item.asset_type;
    const cover = item.cover_image
      || (Array.isArray(item.gallery_images) && item.gallery_images[0])
      || (Array.isArray(item.images) && item.images[0])
      || null;
    let title = item.title || item.name || item.service_name || 'Untitled';
    if (config?.endpoint === '/partner/cars' && (item.brand || item.model)) {
      title = [item.brand, item.model].filter(Boolean).join(' ') || title;
    }
    if (config?.endpoint === '/partner/yachts' && (item.brand || item.name)) {
      title = item.name || [item.brand, item.model].filter(Boolean).join(' ') || title;
    }
    const subtitle = item.category || item.style || item.brand || item.length_ft
      ? [item.category, item.style, item.brand && !title.includes(item.brand) ? item.brand : null,
         item.length_ft ? `${item.length_ft} ft` : null].filter(Boolean).join(' · ')
      : '';
    const price = item.price ?? item.price_from ?? null;
    const status = item.status || (item.published ? 'published' : (isV2 ? 'draft' : 'published'));
    return { isV2, cover, title, subtitle, description: item.description || item.short_description, price, status };
  }, [config]);

  useEffect(() => {
    const userData = JSON.stringify(getStoredUser());
    if (userData) {
      const parsed = JSON.parse(userData);
      setUser(parsed);
      const dashConfig = DASHBOARD_CONFIG[parsed.role] || DASHBOARD_CONFIG.other_business;
      setConfig(dashConfig);
    }
    fetchData();
    
    const interval = setInterval(() => {
      setCurrentBgIndex((prev) => (prev + 1) % 3);
    }, 8000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleCreateItem = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}${config.endpoint}/${editingItem.id}`, newItem, { headers: getAuthHeaders() });
        toast.success(`${config.itemName} updated!`);
        setShowAddModal(false);
        setEditingItem(null);
        setNewItem({});
      } else {
        const res = await axios.post(`${API}${config.endpoint}`, newItem, { headers: getAuthHeaders() });
        toast.success(`${config.itemName} added — you can now upload images/videos.`);
        // Stay in edit-mode so partner can attach media immediately.
        setEditingItem(res.data);
        setNewItem(p => ({ ...p, images: res.data.images || [], videos: res.data.videos || [] }));
      }
      fetchData();
    } catch (error) {
      toast.error(editingItem ? `Failed to update` : `Failed to add ${config.itemName.toLowerCase()}`);
    }
  };

  const handleEditItem = (item) => {
    // v2 listings (asset_type set) open in the new wizard
    const wt = WIZARD_ASSET_TYPE_FROM_ENDPOINT[config.endpoint];
    if (item.asset_type && wt) {
      navigate(`/partner/listings/${wt}/${item.id}`);
      return;
    }
    setEditingItem(item);
    const prefilled = {};
    config.fields.forEach(f => { prefilled[f] = item[f] || ''; });
    prefilled.location = item.location || '';
    prefilled.latitude = item.latitude ?? '';
    prefilled.longitude = item.longitude ?? '';
    prefilled.images = item.images || [];
    prefilled.videos = item.videos || [];
    setNewItem(prefilled);
    setShowAddModal(true);
  };

  const handleDeleteItem = async (itemId) => {
    try {
      const token = getAuthToken();
      await axios.delete(`${API}${config.endpoint}/${itemId}`, { headers: getAuthHeaders() });
      toast.success(`${config.itemName} deleted`);
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error(`Failed to delete ${config.itemName.toLowerCase()}`);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map(i => i.id)));
    }
  };

  const handleBulkDelete = async () => {
    try {
      const token = getAuthToken();
      const ids = Array.from(selectedIds);
      await axios.post(`${API}${config.endpoint}/bulk-delete`, { ids }, { headers: getAuthHeaders() });
      toast.success(`${ids.length} ${config.itemsName.toLowerCase()} deleted`);
      setSelectedIds(new Set());
      setBulkDeleteConfirm(false);
      fetchData();
    } catch (error) {
      toast.error(`Failed to delete ${config.itemsName.toLowerCase()}`);
    }
  };

  if (loading || !config) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-gold text-xl">Loading dashboard...</div>
      </div>
    );
  }

  const Icon = config.icon;
  const accentColorClass = {
    cyan: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', glow: 'bg-cyan-500/10', gradient: 'from-cyan-500 to-cyan-600', shadow: 'shadow-cyan-500/25' },
    pink: { bg: 'bg-pink-500/20', text: 'text-pink-400', glow: 'bg-pink-500/10', gradient: 'from-pink-500 to-pink-600', shadow: 'shadow-pink-500/25' },
    amber: { bg: 'bg-amber-500/20', text: 'text-amber-400', glow: 'bg-amber-500/10', gradient: 'from-amber-500 to-amber-600', shadow: 'shadow-amber-500/25' }
  }[config.accentColor] || { bg: 'bg-gold/20', text: 'text-gold', glow: 'bg-gold/10', gradient: 'from-gold to-gold-light', shadow: 'shadow-gold/25' };

  return (
    <div className="min-h-screen bg-obsidian text-text-primary">
      {/* Navigation */}
      <Nav variant="partner" showBack partnerLabel={config.portal} companyName={user?.company_name} />

      {/* Hero Section with Luxury Background */}
      <div className="relative h-[320px] overflow-hidden" data-testid="generic-hero">
        {config.heroImages.map((img, index) => (
          <motion.div
            key={img}
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${img})` }}
            initial={{ opacity: 0 }}
            animate={{ opacity: index === currentBgIndex ? 1 : 0 }}
            transition={{ duration: 1.5 }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-obsidian" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />
        
        <div className="relative z-10 max-w-[1440px] mx-auto px-8 h-full flex items-center">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-xl ${accentColorClass.bg} backdrop-blur-sm flex items-center justify-center`}>
                <Icon className={`w-6 h-6 ${accentColorClass.text}`} />
              </div>
              <span className={`${accentColorClass.text} text-sm uppercase tracking-widest opacity-80`}>
                {config.portal.replace(' Portal', '')}
              </span>
            </div>
            <h1 className="font-playfair text-5xl font-semibold mb-3 text-white">{config.title}</h1>
            <p className="text-white/70 text-lg">Welcome back, <span className="text-gold">{user?.name}</span></p>
          </motion.div>
          
          {(activeTab === 'items' || activeTab === 'overview') && (
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="ml-auto flex gap-2"
            >
              {assetType && (
                <Button onClick={() => setBulkUploadOpen(true)} variant="outline" className="border-gold/30 text-gold hover:bg-gold/10" data-testid="bulk-upload-items-btn">
                  <Package className="w-4 h-4 mr-2" /> Bulk Upload
                </Button>
              )}
              <Button onClick={() => {
                const wt = WIZARD_ASSET_TYPE_FROM_ENDPOINT[config.endpoint];
                if (wt) navigate(`/partner/listings/new/${wt}`);
                else setShowAddModal(true);
              }} className="bg-gold text-black hover:bg-gold-light" data-testid="add-item-btn">
                <Plus className="w-4 h-4 mr-2" /> Add {config.itemName}
              </Button>
            </motion.div>
          )}
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-8 py-8 -mt-16 relative z-20">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-testid="generic-stats">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} 
            className={`relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 p-6 backdrop-blur-sm group hover:border-${config.accentColor}-500/30 transition-all duration-300`}>
            <div className={`absolute top-0 right-0 w-32 h-32 ${accentColorClass.glow} rounded-full blur-3xl group-hover:opacity-75 transition-all`} />
            <Icon className={`w-10 h-10 ${accentColorClass.text} mb-4 relative z-10`} />
            <div className="text-4xl font-bold mb-1 relative z-10">{items.length}</div>
            <div className="text-text-secondary">Total {config.itemsName}</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} 
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 p-6 backdrop-blur-sm group hover:border-gold/30 transition-all duration-300">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gold/10 rounded-full blur-3xl group-hover:bg-gold/20 transition-all" />
            <Users className="w-10 h-10 text-gold mb-4 relative z-10" />
            <div className="text-4xl font-bold mb-1 relative z-10">{analytics?.leads?.total || 0}</div>
            <div className="text-text-secondary">Total Leads</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} 
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 p-6 backdrop-blur-sm group hover:border-emerald-500/30 transition-all duration-300">
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl group-hover:bg-emerald-500/20 transition-all" />
            <TrendingUp className="w-10 h-10 text-emerald-400 mb-4 relative z-10" />
            <div className="text-4xl font-bold mb-1 relative z-10">{analytics?.conversion_rate || 0}%</div>
            <div className="text-text-secondary">Conversion Rate</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} 
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 p-6 backdrop-blur-sm group hover:border-gold/30 transition-all duration-300">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gold/10 rounded-full blur-3xl group-hover:bg-gold/20 transition-all" />
            <DollarSign className="w-10 h-10 text-gold mb-4 relative z-10" />
            <div className="text-2xl font-bold mb-1 relative z-10">{formatCurrency(analytics?.total_revenue)}</div>
            <div className="text-text-secondary">Revenue</div>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 bg-white/5 backdrop-blur-sm rounded-xl p-2" data-testid="generic-tabs">
          {['overview', 'items', 'crm', 'reports'].map((tab) => (
            <button 
              key={tab} 
              onClick={() => setActiveTab(tab)} 
              data-testid={`tab-${tab}`}
              className={`px-6 py-3 rounded-lg transition-all duration-300 capitalize font-medium ${
                activeTab === tab 
                  ? `bg-gradient-to-r ${accentColorClass.gradient} text-white shadow-lg ${accentColorClass.shadow}` 
                  : 'text-text-secondary hover:bg-white/10 hover:text-white'
              }`}
            >
              {tab === 'items' ? config.itemsName : tab}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <div className="space-y-8">
            <h3 className="font-playfair text-2xl font-semibold">Recent {config.itemsName}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {items.slice(0, 3).map((item, index) => {
                const v = adapt(item);
                return (
                  <motion.div key={item.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 + index * 0.1 }} className="glass-card overflow-hidden">
                    {v.cover && (
                      <div className="aspect-[16/10] bg-white/5 overflow-hidden">
                        <img src={v.cover} alt="" className="w-full h-full object-cover" loading="lazy" />
                      </div>
                    )}
                    <div className="p-6">
                      <h4 className="font-semibold text-lg mb-2">{v.title}</h4>
                      {v.subtitle && <p className="text-text-secondary text-sm mb-4">{v.subtitle}</p>}
                      {v.price != null && <div className="text-gold font-bold">{formatCurrency(v.price)}</div>}
                    </div>
                  </motion.div>
                );
              })}
              {items.length === 0 && (
                <div className="col-span-3 glass-card p-8 text-center text-text-secondary">
                  No {config.itemsName.toLowerCase()} yet. Add your first one to get started.
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'items' && (
          <div>
            {/* Bulk Actions Toolbar */}
            {items.length > 0 && (
              <div className="flex items-center justify-between mb-6 glass-card p-4">
                <div className="flex items-center gap-4">
                  <button onClick={toggleSelectAll} className="flex items-center gap-2 text-sm text-text-secondary hover:text-white transition-colors" data-testid="select-all-items">
                    {selectedIds.size === items.length && items.length > 0 ? <CheckSquare className="w-5 h-5 text-gold" /> : <Square className="w-5 h-5" />}
                    <span>{selectedIds.size === items.length && items.length > 0 ? 'Deselect All' : 'Select All'}</span>
                  </button>
                  {selectedIds.size > 0 && (
                    <span className="text-sm text-gold font-medium">{selectedIds.size} selected</span>
                  )}
                </div>
                {selectedIds.size > 0 && (
                  <div className="flex items-center gap-2">
                    {bulkDeleteConfirm ? (
                      <>
                        <span className="text-sm text-red-400 mr-2">Delete {selectedIds.size} items?</span>
                        <Button size="sm" onClick={handleBulkDelete} className="bg-red-600 text-white hover:bg-red-700" data-testid="confirm-bulk-delete-items">Yes, Delete</Button>
                        <Button size="sm" variant="outline" onClick={() => setBulkDeleteConfirm(false)} className="border-white/20 text-white hover:bg-white/10">Cancel</Button>
                      </>
                    ) : (
                      <Button size="sm" onClick={() => setBulkDeleteConfirm(true)} className="bg-red-600/80 text-white hover:bg-red-600" data-testid="bulk-delete-items-btn">
                        <Trash2 className="w-4 h-4 mr-1.5" /> Delete Selected ({selectedIds.size})
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((item) => {
              const v = adapt(item);
              const rep = completion[item.id];
              const pct = rep?.total_pct;
              return (
              <div key={item.id} className={`glass-card overflow-hidden hover:border-gold/30 transition-colors relative group ${selectedIds.has(item.id) ? 'border-gold/50 bg-gold/5' : ''}`}>
                {/* Selection Checkbox */}
                <button onClick={() => toggleSelect(item.id)} className="absolute top-4 right-4 z-10" data-testid={`select-item-${item.id}`}>
                  {selectedIds.has(item.id) ? <CheckSquare className="w-5 h-5 text-gold" /> : <Square className="w-5 h-5 text-white/30 group-hover:text-white/60 transition-colors" />}
                </button>
                {v.cover && (
                  <div className="aspect-[16/10] bg-white/5 overflow-hidden">
                    <img src={v.cover} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-start justify-between gap-3 mb-2 pr-8">
                    <h4 className="font-semibold text-lg">{v.title}</h4>
                  </div>
                  {/* Status + completion pill */}
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.15em] font-semibold ring-1 ${
                      v.status === 'published' ? 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/30' : 'bg-amber-500/10 text-amber-300 ring-amber-500/30'
                    }`} data-testid={`status-${item.id}`}>{v.status || 'draft'}</span>
                    {typeof pct === 'number' && (
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.15em] font-semibold ring-1 ${
                          pct >= 100 ? 'bg-gold/15 text-gold ring-gold/40' : 'bg-white/5 text-text-secondary ring-white/15'
                        }`}
                        data-testid={`completion-pill-${item.id}`}
                      >
                        {pct}% complete
                      </span>
                    )}                  </div>
                  {v.subtitle && <p className="text-text-secondary text-sm mb-3">{v.subtitle}</p>}
                  {v.description && <p className="text-text-secondary text-sm mb-4 line-clamp-2">{v.description}</p>}
                  {v.price != null && <div className="text-gold text-xl font-bold">{formatCurrency(v.price)}</div>}
                  {/* Edit / Delete Actions */}
                  <div className="flex gap-2 mt-4 pt-4 border-t border-white/10">
                    <Button variant="outline" size="sm" onClick={() => handleEditItem(item)} className="flex-1 border-white/20 text-white hover:bg-white/10 hover:text-gold" data-testid={`edit-item-${item.id}`}>
                      <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                    </Button>
                    {deleteConfirm === item.id ? (
                      <div className="flex gap-1 flex-1">
                        <Button size="sm" onClick={() => handleDeleteItem(item.id)} className="flex-1 bg-red-600 text-white hover:bg-red-700" data-testid={`confirm-delete-item-${item.id}`}>Yes</Button>
                        <Button size="sm" variant="outline" onClick={() => setDeleteConfirm(null)} className="flex-1 border-white/20 text-white hover:bg-white/10">No</Button>
                      </div>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(item.id)} className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300" data-testid={`delete-item-${item.id}`}>
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
            })}
            {items.length === 0 && (
              <div className="col-span-3 glass-card p-12 text-center">
                <Icon className="w-16 h-16 text-gold mx-auto mb-4 opacity-50" />
                <h3 className="font-playfair text-2xl font-semibold mb-2">No {config.itemsName} Yet</h3>
                <p className="text-text-secondary mb-6">Add your first {config.itemName.toLowerCase()} to get started</p>
                <Button
                  onClick={() => {
                    const wt = WIZARD_ASSET_TYPE_FROM_ENDPOINT[config.endpoint];
                    if (wt) navigate(`/partner/listings/new/${wt}`);
                    else setShowAddModal(true);
                  }}
                  className="bg-gold text-black hover:bg-gold-light"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add {config.itemName}
                </Button>
              </div>
            )}
          </div>
          </div>
        )}

        {activeTab === 'crm' && (
          <div className="space-y-6">
            <PartnerCRMDashboardV2 />
            <PartnerCRM partnerId={user?.id} />
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="glass-card p-8 text-center">
            <BarChart3 className="w-16 h-16 text-gold mx-auto mb-4 opacity-50" />
            <h3 className="font-playfair text-2xl font-semibold mb-2">Reports Coming Soon</h3>
            <p className="text-text-secondary">Detailed analytics will be available soon.</p>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-lenis-prevent>
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }} 
            animate={{ opacity: 1, scale: 1 }} 
            className="glass-card p-8 max-w-lg w-full max-h-[85vh] overflow-y-auto overscroll-contain"
            style={{ scrollbarGutter: 'stable' }}
            data-lenis-prevent
          >
            <div className="flex justify-between items-center mb-6 sticky top-0 bg-obsidian/90 backdrop-blur-sm pb-4 -mt-2 pt-2 z-10">
              <h2 className="font-playfair text-2xl font-semibold">{editingItem ? 'Edit' : 'Add'} {config.itemName}</h2>
              <Button variant="ghost" onClick={() => { setShowAddModal(false); setEditingItem(null); }}><X className="w-5 h-5" /></Button>
            </div>
            <div className="space-y-4">
              {config.fields.map((field) => (
                <div key={field}>
                  <Label className="capitalize">{field.replace(/_/g, ' ')}</Label>
                  {field === 'description' ? (
                    <textarea value={newItem[field] || ''} onChange={(e) => setNewItem({...newItem, [field]: e.target.value})} className="w-full bg-white text-black rounded p-3 min-h-[80px]" />
                  ) : field === 'price_type' ? (
                    <StyledSelect
                      value={newItem[field] || 'fixed'}
                      onChange={(v) => setNewItem({...newItem, [field]: v})}
                      options={[
                        { value: 'fixed', label: 'Fixed' },
                        { value: 'hourly', label: 'Hourly' },
                        { value: 'negotiable', label: 'Negotiable' },
                      ]}
                      data-testid={`generic-${field}-select`}
                    />
                  ) : (
                    <Input type={['price', 'length_ft', 'year', 'capacity'].includes(field) ? 'number' : 'text'} value={newItem[field] || ''} onChange={(e) => setNewItem({...newItem, [field]: e.target.value})} className="bg-white text-black" />
                  )}
                </div>
              ))}

              {/* Location & Map */}
              <div className="pt-2 border-t border-white/10">
                <LocationField
                  testIdPrefix="item"
                  location={newItem.location || ''}
                  latitude={newItem.latitude ?? ''}
                  longitude={newItem.longitude ?? ''}
                  onChange={(loc) => setNewItem(p => ({ ...p, ...loc }))}
                />
              </div>

              {/* Media (image + video) — only after asset is saved */}
              {assetType && (
                <div className="pt-2 border-t border-white/10">
                  <Label className="text-text-primary mb-2 block">Photos &amp; Videos</Label>
                  <AssetMediaManager
                    assetType={assetType}
                    assetId={editingItem?.id || null}
                    images={newItem.images || []}
                    videos={newItem.videos || []}
                    onChange={(media) => setNewItem(p => ({ ...p, ...media }))}
                  />
                </div>
              )}

              <div className="pt-4 pb-2">
                <Button onClick={handleCreateItem} className="w-full bg-gold text-black hover:bg-gold-light" data-testid="save-item-btn">
                  {editingItem ? 'Update' : 'Add'} {config.itemName}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {assetType && (
        <BulkUploadModal
          open={bulkUploadOpen}
          assetType={assetType}
          onClose={() => setBulkUploadOpen(false)}
          onComplete={fetchData}
        />
      )}
    </div>
  );
};

export default GenericPartnerDashboard;
